import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Database, 
  Download, 
  Upload, 
  Trash2, 
  RefreshCw, 
  HardDrive,
  Wifi,
  WifiOff,
  Clock,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react'
import { offlineStorageService } from '../../services/offlineStorage'
import { offlineSyncService } from '../../services/offlineSync'
import { useNetworkStatus } from '../../hooks/useCache'

const OfflineDataManager: React.FC = () => {
  const { isOnline } = useNetworkStatus()
  const [stats, setStats] = useState(offlineStorageService.getStorageStats())
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [syncStatus, setSyncStatus] = useState(offlineSyncService.getStatus())

  useEffect(() => {
    const unsubscribe = offlineSyncService.addStatusListener(setSyncStatus)
    return unsubscribe
  }, [])

  const refreshStats = () => {
    setStats(offlineStorageService.getStorageStats())
  }

  const handlePreloadData = async () => {
    if (!isOnline) {
      setMessage('需要网络连接才能预加载数据')
      return
    }

    setIsLoading(true)
    setMessage('')
    
    try {
      await offlineSyncService.preloadCriticalData()
      refreshStats()
      setMessage('数据预加载完成')
    } catch (error) {
      setMessage('数据预加载失败')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClearData = (type?: string) => {
    if (type) {
      offlineStorageService.clearDataByType(type as any)
    } else {
      offlineStorageService.clearAllData()
    }
    refreshStats()
    setMessage(`${type ? type : '所有'}数据已清理`)
  }

  const handleCleanExpired = () => {
    offlineStorageService.cleanExpiredData()
    refreshStats()
    setMessage('过期数据已清理')
  }

  const handleSync = async () => {
    if (!isOnline) {
      setMessage('需要网络连接才能同步数据')
      return
    }

    setIsLoading(true)
    setMessage('')
    
    try {
      await offlineSyncService.forceSync()
      setMessage('数据同步完成')
    } catch (error) {
      setMessage('数据同步失败')
    } finally {
      setIsLoading(false)
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const dataTypes = [
    { key: 'news', name: '新闻', icon: Database },
    { key: 'products', name: '产品', icon: Database },
    { key: 'solutions', name: '解决方案', icon: Database },
    { key: 'careers', name: '招聘', icon: Database },
    { key: 'faq', name: 'FAQ', icon: Database },
    { key: 'contact', name: '联系', icon: Database }
  ]

  return (
    <div className="space-y-6">
      {/* 状态概览 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <HardDrive className="w-5 h-5 mr-2" />
            离线数据管理
          </h3>
          <div className="flex items-center space-x-2">
            {isOnline ? (
              <Wifi className="w-4 h-4 text-green-500" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-500" />
            )}
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {isOnline ? '在线' : '离线'}
            </span>
          </div>
        </div>

        {/* 统计信息 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {stats.totalItems}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">总项目数</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {formatFileSize(stats.totalSize)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">存储大小</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {syncStatus.pendingSync}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">待同步</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {syncStatus.lastSyncTime ? 
                new Date(syncStatus.lastSyncTime).toLocaleDateString() : 
                '从未'
              }
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">最后同步</div>
          </div>
        </div>

        {/* 消息提示 */}
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center"
          >
            <Info className="w-4 h-4 text-blue-500 mr-2" />
            <span className="text-blue-700 dark:text-blue-300 text-sm">{message}</span>
          </motion.div>
        )}

        {/* 操作按钮 */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handlePreloadData}
            disabled={!isOnline || isLoading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            {isLoading ? '预加载中...' : '预加载数据'}
          </button>
          
          <button
            onClick={handleSync}
            disabled={!isOnline || isLoading || syncStatus.isSyncing}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Upload className="w-4 h-4 mr-2" />
            {syncStatus.isSyncing ? '同步中...' : '同步数据'}
          </button>
          
          <button
            onClick={handleCleanExpired}
            className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
          >
            <Clock className="w-4 h-4 mr-2" />
            清理过期
          </button>
          
          <button
            onClick={() => handleClearData()}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            清空所有
          </button>
        </div>
      </div>

      {/* 数据类型统计 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          数据类型统计
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {dataTypes.map((type) => {
            const Icon = type.icon
            const count = stats.typeCounts[type.key] || 0
            
            return (
              <div
                key={type.key}
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <Icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <span className="text-gray-900 dark:text-white">{type.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {count} 项
                  </span>
                  <button
                    onClick={() => handleClearData(type.key)}
                    className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    title={`清空${type.name}数据`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* 同步状态 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          同步状态
        </h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">网络状态</span>
            <div className="flex items-center space-x-2">
              {isOnline ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <AlertCircle className="w-4 h-4 text-red-500" />
              )}
              <span className="text-sm text-gray-900 dark:text-white">
                {isOnline ? '在线' : '离线'}
              </span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">同步状态</span>
            <div className="flex items-center space-x-2">
              {syncStatus.isSyncing ? (
                <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 text-green-500" />
              )}
              <span className="text-sm text-gray-900 dark:text-white">
                {syncStatus.isSyncing ? '同步中' : '空闲'}
              </span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">待同步项目</span>
            <span className="text-sm text-gray-900 dark:text-white">
              {syncStatus.pendingSync} 项
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">最后同步时间</span>
            <span className="text-sm text-gray-900 dark:text-white">
              {syncStatus.lastSyncTime ? 
                new Date(syncStatus.lastSyncTime).toLocaleString() : 
                '从未同步'
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OfflineDataManager